import {faCircleCheck, faPlus, faTrashCan} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FC, useState } from "react";
import {
  InspectionComment,
  type InspectionFinding,
  type InspectionSeverity,
  inspectionSeverityOptions,
} from "../api/dto/InspectionFinding";
import { Input } from "../components/form/Input";
import { Select } from "../components/form/Select";
import { Toggle } from "../components/form/Toggle";
import { ModalFooter } from "../components/layout/ModalProvider";

const filterSeverityOptions = (allowed: InspectionSeverity[]): { [key: string]: string } =>
  Object.fromEntries(
    Object.entries(inspectionSeverityOptions).filter(([key]) =>
      allowed.includes(key as InspectionSeverity),
    ),
  );

export const EditInspectionFindingModal: FC<{
  finding: InspectionFinding;
  availableSeverities: InspectionSeverity[];
  onStage: (finding: {
    id: string;
    questionId: string;
    severity: InspectionSeverity;
    description: string;
    comments: InspectionComment[];
  }) => void;
}> = (props) => {
  // If the finding's current severity is no longer one of the allowed severities (e.g. the
  // question's answer changed after this finding was created), don't keep the stale value -
  // default to the first allowed one instead.
  const [severity, setSeverity] = useState<InspectionSeverity>(
    props.availableSeverities.includes(props.finding.severity)
      ? props.finding.severity
      : (props.availableSeverities[0] ?? props.finding.severity),
  );
  const [description, setDescription] = useState<string>(props.finding.description);
  const [comments, setComments] = useState<InspectionComment[]>(props.finding.comments);

  const save = () => {
    props.onStage({
      id: props.finding.id,
      questionId: props.finding.inspectionQuestionId,
      severity,
      description,
      comments,
    });
  };
  return (
    <div>
      <div className={"grid grid-cols-1 xl:grid-cols-2 gap-4"}>
        <div className={"flex flex-col items-stretch space-y-3"}>
          <div className={"h-10 text-lg font-medium flex items-center"}>Bevinding</div>
          <Select
            label={"Klassificatie"}
            options={filterSeverityOptions(props.availableSeverities)}
            value={severity}
            onChange={(v) => setSeverity(v as InspectionSeverity)}
          />
          <Input
            type={"textarea"}
            label={"Toelichting"}
            value={description}
            onChange={setDescription}
            maxWidth={"full"}
            minHeight={"500"}
          />
        </div>

        <div className={"flex flex-col items-stretch space-y-3"}>
          <div className={"h-10 text-lg font-medium flex items-center"}>Reacties</div>

          {comments.map((comment, idx) => (
            <div className="flex flex-col items-stretch space-y-3 relative border border-slate-200 rounded-xl p-8 bg-white">
              <Input
                type={"text"}
                label={"Opgelost door"}
                value={comment.user ?? ""}
                onChange={(val) =>
                  setComments(comments.toSpliced(idx, 1, { ...comment, user: val }))
                }
              />

              <div className={"grid grid-cols-1 lg:grid-cols-2 gap-3"}>
                <Input
                  type={"date"}
                  label={"Opgelost op"}
                  value={comment.isFixedAt ?? new Date()}
                  onChange={(v) => {
                    const newDate = comment.isFixedAt ?? new Date();
                    newDate.setFullYear(v.getFullYear(), v.getMonth(), v.getDate());
                    setComments(
                      comments.toSpliced(idx, 1, { ...comment, isFixedAt: new Date(newDate) }),
                    );
                  }}
                />

                <Input
                  type={"time"}
                  label={"Opgelost op"}
                  value={comment.isFixedAt ?? new Date()}
                  onChange={(v) => {
                    const newDate = comment.isFixedAt ?? new Date();
                    newDate.setHours(v.getHours());
                    newDate.setMinutes(v.getMinutes());
                    setComments(
                      comments.toSpliced(idx, 1, { ...comment, isFixedAt: new Date(newDate) }),
                    );
                  }}
                />
              </div>

              <Input
                type={"textarea"}
                label={"Reactie"}
                value={comment.comment}
                onChange={(val) =>
                  setComments(comments.toSpliced(idx, 1, { ...comment, comment: val }))
                }
              />

              <Toggle
                value={comment.isFixedWithin24Hours}
                label={"Opgelost binnen 24 uur"}
                onChange={(val) =>
                  setComments(comments.toSpliced(idx, 1, { ...comment, isFixedWithin24Hours: val }))
                }
              />

              <button
                className="absolute top-2.5 right-2.5 w-7 h-7 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50"
                onClick={() => setComments(comments.toSpliced(idx, 1))}
              >
                <FontAwesomeIcon icon={faTrashCan} />
              </button>
            </div>
          ))}

          <button
            onClick={() =>
              setComments([...comments, new InspectionComment("", "", null, false, null)])
            }
            className="w-full h-10 flex items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 text-brand-900 text-sm font-medium hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faPlus} />
            Voeg reactie toe
          </button>
        </div>
      </div>

      <ModalFooter
        text={"Bevestig"}
        icon={faCircleCheck}
        onSubmit={save}
        disabled={
          description.trim().length === 0 ||
          !!comments.find((f) => f.comment.trim().length === 0 || f.user.trim().length === 0)
        }
      />
    </div>
  );
};
